import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { z } from "zod";
import { ArrowRight, ArrowLeft, CheckCircle2, Mail, Building2, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const emailSchema = z.string().trim().email("Enter a valid email").max(255);
const nameSchema = z.string().trim().min(1, "Your name is required").max(100);
const clubSchema = z.string().trim().min(1, "Club name is required").max(200);

const ROLES = ["Chair", "Secretary", "Treasurer", "Captain", "Coach", "Other"] as const;
const PRIORITIES = [
  { id: "website", label: "A modern club website", desc: "Sharp, branded, always up to date." },
  { id: "fees", label: "Match fees & subscriptions", desc: "Collect online, chase nothing." },
  { id: "comms", label: "Member communications", desc: "Push, email, all in one place." },
  { id: "fixtures", label: "Fixtures & availability", desc: "Synced from Play-Cricket, automated." },
  { id: "all", label: "All of the above", desc: "The full platform — every feature." },
];

interface RegisterInterestButtonProps {
  label?: string;
  className?: string;
  variant?: "primary" | "outline" | "dark" | "ghost-light";
  size?: "default" | "lg";
  source?: string;
  children?: React.ReactNode;
  /** When provided, switches to controlled mode (no trigger button rendered). */
  controlledOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the default trigger button (used by controlled callers). */
  triggerless?: boolean;
}

const variantClasses: Record<string, string> = {
  primary: "bg-cricket-red text-cricket-red-foreground hover:opacity-90",
  outline: "border border-border bg-background text-foreground hover:bg-accent",
  dark: "bg-foreground text-background hover:opacity-90",
  "ghost-light":
    "border border-primary-foreground/20 bg-primary-foreground/5 text-primary-foreground hover:bg-primary-foreground/10",
};

const sizeClasses: Record<string, string> = {
  default: "px-6 py-3 text-sm",
  lg: "px-8 py-4 text-base",
};

const RegisterInterestButton = ({
  label = "Register Interest",
  className,
  variant = "primary",
  size = "lg",
  source = "register-interest",
  children,
  controlledOpen,
  onOpenChange,
  triggerless,
}: RegisterInterestButtonProps) => {
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (v: boolean) => {
    if (!isControlled) setInternalOpen(v);
    onOpenChange?.(v);
  };
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    email: "",
    name: "",
    club_name: "",
    role: "" as (typeof ROLES)[number] | "",
    priority: "" as string,
  });
  const firstInputRef = useRef<HTMLInputElement | null>(null);

  // Autofocus first input on each step
  useEffect(() => {
    if (open && !done) {
      const t = setTimeout(() => firstInputRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [open, step, done]);

  const reset = () => {
    setStep(0);
    setError("");
    setDone(false);
    setForm({ email: "", name: "", club_name: "", role: "", priority: "" });
  };

  const next = () => {
    setError("");
    if (step === 0) {
      const r = emailSchema.safeParse(form.email);
      if (!r.success) {
        setError(r.error.issues[0].message);
        return;
      }
    }
    if (step === 1) {
      const n = nameSchema.safeParse(form.name);
      if (!n.success) {
        setError(n.error.issues[0].message);
        return;
      }
      const c = clubSchema.safeParse(form.club_name);
      if (!c.success) {
        setError(c.error.issues[0].message);
        return;
      }
      if (!form.role) {
        setError("Pick the role that best fits you");
        return;
      }
    }
    setStep((s) => s + 1);
  };

  const back = () => {
    setError("");
    setStep((s) => Math.max(0, s - 1));
  };

  const submit = async () => {
    if (!form.priority) {
      setError("Pick what matters most to your club");
      return;
    }
    setSubmitting(true);
    setError("");

    const payload = {
      email: form.email.trim(),
      name: form.name.trim() || null,
      club_name: form.club_name.trim() || null,
      role: form.role || null,
      priority: form.priority || null,
      source,
    };

    const { error: insertError } = await supabase.from("waitlist").insert([payload]);

    let alreadyRegistered = false;
    if (insertError) {
      if (insertError.code === "23505") {
        alreadyRegistered = true;
      } else {
        setError("Something went wrong. Please try again.");
        setSubmitting(false);
        return;
      }
    }

    // Fire notification + welcome (don't block UX on response)
    supabase.functions
      .invoke("notify-waitlist-signup", {
        body: { ...payload, already_registered: alreadyRegistered },
      })
      .catch((err) => console.warn("notify-waitlist-signup failed", err));

    setSubmitting(false);
    setDone(true);
    toast.success(alreadyRegistered ? "You were already on the list — thanks!" : "You're in. Check your inbox.");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setTimeout(reset, 200);
      }}
    >
      {!triggerless && (
        <DialogTrigger asChild>
          {children ?? (
            <button
              type="button"
              className={cn(
                "inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-opacity",
                variantClasses[variant],
                sizeClasses[size],
                className,
              )}
            >
              {label}
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </DialogTrigger>
      )}

      <DialogContent className="sm:max-w-lg p-0 overflow-hidden gap-0">
        {/* Accent bar */}
        <div className="h-1 w-full bg-cricket-red" />

        <div className="px-7 pt-7 pb-6">
          {/* Progress */}
          {!done && (
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-1.5">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className={cn(
                      "h-1.5 rounded-full transition-all duration-300",
                      i === step ? "w-8 bg-cricket-red" : i < step ? "w-4 bg-cricket-red/60" : "w-4 bg-border",
                    )}
                  />
                ))}
              </div>
              <span className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
                Step {step + 1} of 3
              </span>
            </div>
          )}

          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="py-6 text-center"
            >
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-cricket-red/10">
                <CheckCircle2 className="h-7 w-7 text-cricket-red" />
              </div>
              <DialogTitle className="text-2xl font-bold mb-2">You're in.</DialogTitle>
              <DialogDescription className="text-base text-muted-foreground mb-6">
                We've just sent over your early access details. Check your inbox — including spam, just in case.
              </DialogDescription>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex h-11 items-center justify-center rounded-lg bg-foreground px-6 text-sm font-semibold text-background hover:opacity-90 transition-opacity"
              >
                Close
              </button>
            </motion.div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
              >
                {/* STEP 0 */}
                {step === 0 && (
                  <div>
                    <DialogTitle className="text-2xl font-bold leading-tight mb-2">
                      Let's get you early access.
                    </DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground mb-6">
                      Built for clubs that take the season seriously. Early access clubs get £200 off.
                    </DialogDescription>
                    <label className="block text-xs font-semibold text-foreground mb-2">
                      <Mail className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5 text-muted-foreground" />
                      Your email
                    </label>
                    <input
                      ref={firstInputRef}
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && next()}
                      placeholder="you@yourclub.co.uk"
                      maxLength={255}
                      className="w-full h-12 rounded-lg border border-input bg-background px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-cricket-red/40 focus:border-cricket-red/60 transition-all"
                    />
                    {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
                    <button
                      type="button"
                      onClick={next}
                      className="mt-5 w-full h-12 rounded-lg bg-cricket-red text-cricket-red-foreground font-semibold text-sm flex items-center justify-center gap-2 hover:brightness-110 transition-all"
                    >
                      Continue
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {/* STEP 1 */}
                {step === 1 && (
                  <div>
                    <DialogTitle className="text-2xl font-bold leading-tight mb-2">
                      Tell us about your club.
                    </DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground mb-6">
                      So we can tailor what we share with you.
                    </DialogDescription>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-semibold text-foreground mb-2">Your name</label>
                        <input
                          ref={firstInputRef}
                          type="text"
                          value={form.name}
                          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                          placeholder="Jane Smith"
                          maxLength={100}
                          className="w-full h-11 rounded-lg border border-input bg-background px-4 text-sm focus:outline-none focus:ring-2 focus:ring-cricket-red/40 focus:border-cricket-red/60 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-foreground mb-2">
                          <Building2 className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5 text-muted-foreground" />
                          Club name
                        </label>
                        <input
                          type="text"
                          value={form.club_name}
                          onChange={(e) => setForm((f) => ({ ...f, club_name: e.target.value }))}
                          placeholder="e.g. Bishops Cannings CC"
                          maxLength={200}
                          className="w-full h-11 rounded-lg border border-input bg-background px-4 text-sm focus:outline-none focus:ring-2 focus:ring-cricket-red/40 focus:border-cricket-red/60 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-foreground mb-2">Your role</label>
                        <div className="flex flex-wrap gap-2">
                          {ROLES.map((r) => (
                            <button
                              key={r}
                              type="button"
                              onClick={() => setForm((f) => ({ ...f, role: r }))}
                              className={cn(
                                "px-3.5 py-2 rounded-full text-xs font-semibold border transition-all",
                                form.role === r
                                  ? "bg-cricket-red text-cricket-red-foreground border-cricket-red"
                                  : "bg-background text-foreground border-border hover:border-cricket-red/50 hover:text-cricket-red",
                              )}
                            >
                              {r}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {error && <p className="mt-3 text-xs text-destructive">{error}</p>}

                    <div className="mt-6 flex items-center gap-3">
                      <button
                        type="button"
                        onClick={back}
                        className="h-11 px-4 rounded-lg border border-border bg-background text-sm font-semibold text-foreground hover:bg-accent transition-all flex items-center gap-1.5"
                      >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={next}
                        className="flex-1 h-11 rounded-lg bg-cricket-red text-cricket-red-foreground font-semibold text-sm flex items-center justify-center gap-2 hover:brightness-110 transition-all"
                      >
                        Continue
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* STEP 2 */}
                {step === 2 && (
                  <div>
                    <DialogTitle className="text-2xl font-bold leading-tight mb-2">
                      What matters most right now?
                    </DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground mb-5">
                      Pick the one that would change the most for your club.
                    </DialogDescription>

                    <div className="space-y-2">
                      {PRIORITIES.map((p) => {
                        const selected = form.priority === p.id;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => setForm((f) => ({ ...f, priority: p.id }))}
                            className={cn(
                              "w-full text-left p-3.5 rounded-lg border transition-all flex items-start gap-3",
                              selected
                                ? "bg-cricket-red/5 border-cricket-red"
                                : "bg-background border-border hover:border-cricket-red/40",
                            )}
                          >
                            <span
                              className={cn(
                                "mt-0.5 h-4 w-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all",
                                selected ? "border-cricket-red bg-cricket-red" : "border-border",
                              )}
                            >
                              {selected && <span className="h-1.5 w-1.5 rounded-full bg-cricket-red-foreground" />}
                            </span>
                            <span className="flex-1 min-w-0">
                              <span className="block text-sm font-semibold text-foreground">{p.label}</span>
                              <span className="block text-xs text-muted-foreground mt-0.5">{p.desc}</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {error && <p className="mt-3 text-xs text-destructive">{error}</p>}

                    <div className="mt-6 flex items-center gap-3">
                      <button
                        type="button"
                        onClick={back}
                        disabled={submitting}
                        className="h-11 px-4 rounded-lg border border-border bg-background text-sm font-semibold text-foreground hover:bg-accent transition-all flex items-center gap-1.5 disabled:opacity-50"
                      >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={submit}
                        disabled={submitting}
                        className="flex-1 h-11 rounded-lg bg-cricket-red text-cricket-red-foreground font-semibold text-sm flex items-center justify-center gap-2 hover:brightness-110 transition-all disabled:opacity-60"
                      >
                        {submitting ? (
                          "Submitting…"
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4" />
                            Register My Interest
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RegisterInterestButton;
